(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-vi-product-library]';
  var DEFAULT_ENDPOINT = '/apps/horeca-tools/customer/product-library';
  var TIMEOUT_MS = 12000;

  function createApiError(type, status, payload) {
    var error = new Error(type || 'product_library_error');
    error.type = type || 'product_library_error';
    error.status = status || 0;
    error.payload = payload || null;
    return error;
  }

  function fetchJson(endpoint) {
    return new Promise(function (resolve, reject) {
      var controller = window.AbortController ? new AbortController() : null;
      var settled = false;
      var timeoutId = window.setTimeout(function () {
        if (settled) return;
        settled = true;
        if (controller) controller.abort();
        reject(createApiError('timeout', 0));
      }, TIMEOUT_MS);

      var options = {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      };

      if (controller) {
        options.signal = controller.signal;
      }

      window.fetch(endpoint, options)
        .then(function (response) {
          return response.text().then(function (body) {
            var payload = null;
            try {
              payload = body ? JSON.parse(body) : null;
            } catch (parseError) {
              throw createApiError('invalid_json', response.status);
            }

            if (!response.ok) {
              throw createApiError('http_error', response.status, payload);
            }

            if (!payload || payload.ok !== true) {
              throw createApiError('api_error', response.status, payload);
            }

            return payload;
          });
        })
        .then(function (payload) {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeoutId);
          resolve(payload);
        })
        .catch(function (error) {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeoutId);
          reject(error && error.name === 'AbortError' ? createApiError('timeout', 0) : error);
        });
    });
  }

  function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function isValidResponse(payload) {
    return isObject(payload) && payload.ok === true && isObject(payload.data) && Array.isArray(payload.data.products);
  }

  function isAccessError(error) {
    var status = Number(error && error.status);
    var payload = error && error.payload;
    var code = '';

    if (payload) {
      code = String(payload.code || payload.error || payload.reason || '').toLowerCase();
      if (payload.error && typeof payload.error === 'object') {
        code = String(payload.error.code || payload.error.reason || '').toLowerCase();
      }
    }

    return status === 403 || code.indexOf('forbidden') !== -1 || code.indexOf('no_access') !== -1 || code.indexOf('business') !== -1;
  }

  function text(value, fallback) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number') return String(value);
    return fallback || '';
  }

  function isHttpsUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return false;
    try {
      return new URL(value, window.location.origin).protocol === 'https:';
    } catch (error) {
      return false;
    }
  }

  function formatDate(value) {
    var raw = text(value, '');
    var date;

    if (!raw) return 'Ej angivet';
    date = new Date(raw);

    if (Number.isNaN(date.getTime())) return raw;

    try {
      return date.toLocaleDateString('sv-SE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return raw;
    }
  }

  function formatDocumentCount(count) {
    return count === 1 ? '1 dokument' : count + ' dokument';
  }

  function createElement(tagName, className, textContent) {
    var element = document.createElement(tagName);
    if (className) element.className = className;
    if (textContent !== undefined && textContent !== null) element.textContent = textContent;
    return element;
  }

  function clear(element) {
    while (element && element.firstChild) {
      element.removeChild(element.firstChild);
    }
  }

  function setHidden(element, hidden) {
    if (!element) return;
    element.hidden = hidden;
  }

  function showState(root, stateName) {
    var states = root.querySelectorAll('[data-vipl-state]');
    var grid = root.querySelector('[data-vipl-grid]');
    var summary = root.querySelector('[data-vipl-summary]');

    Array.prototype.forEach.call(states, function (state) {
      setHidden(state, state.getAttribute('data-vipl-state') !== stateName);
    });

    setHidden(grid, true);
    setHidden(summary, true);
  }

  function showProducts(root, count, documentCount) {
    var states = root.querySelectorAll('[data-vipl-state]');
    var grid = root.querySelector('[data-vipl-grid]');
    var summary = root.querySelector('[data-vipl-summary]');
    var productCountTarget = root.querySelector('[data-vipl-product-count]');
    var documentCountTarget = root.querySelector('[data-vipl-document-count]');

    Array.prototype.forEach.call(states, function (state) {
      setHidden(state, true);
    });

    if (productCountTarget) {
      productCountTarget.textContent = count === 1 ? '1 produkt' : count + ' produkter';
    }

    if (documentCountTarget) {
      documentCountTarget.textContent = formatDocumentCount(documentCount);
    }

    setHidden(summary, false);
    setHidden(grid, false);
  }

  function renderImage(product, title) {
    var media = createElement('div', 'vi-product-library__media');
    var imageUrl = text(product.imageUrl, '');

    if (isHttpsUrl(imageUrl)) {
      var image = document.createElement('img');
      image.src = imageUrl;
      image.alt = title;
      image.loading = 'lazy';
      image.decoding = 'async';
      media.appendChild(image);
      return media;
    }

    media.classList.add('vi-product-library__media--empty');
    media.appendChild(createElement('span', 'vi-product-library__image-fallback', 'Produkt'));
    return media;
  }

  function renderMeta(label, value) {
    var item = createElement('div', 'vi-product-library__meta-item');
    item.appendChild(createElement('dt', 'vi-product-library__meta-label', label));
    item.appendChild(createElement('dd', 'vi-product-library__meta-value', value));
    return item;
  }

  function renderDocument(documentData) {
    var title = text(documentData && documentData.title, 'Dokument');
    var fileType = text(documentData && documentData.fileType, 'Fil').toUpperCase();
    var updatedAt = formatDate(documentData && documentData.updatedAt);
    var fileUrl = text(documentData && documentData.fileUrl, '');
    var hasValidLink = isHttpsUrl(fileUrl);
    var card = createElement('article', 'vi-product-library__document-card');
    var body = createElement('div', 'vi-product-library__document-body');
    var titleElement = createElement('h3', 'vi-product-library__document-title', title);
    var meta = createElement('p', 'vi-product-library__document-meta');
    var actionWrap = createElement('div', 'vi-product-library__document-actions');

    meta.appendChild(createElement('span', 'vi-product-library__file-type', fileType));
    meta.appendChild(document.createTextNode('Uppdaterad ' + updatedAt));

    body.appendChild(titleElement);
    body.appendChild(meta);

    if (documentData && documentData.b2bOnly === true) {
      body.appendChild(createElement('span', 'vi-product-library__document-badge', 'Företagsdokument'));
    }

    if (hasValidLink) {
      var link = document.createElement('a');
      link.className = 'vi-button-pro vi-product-library__document-link';
      link.href = fileUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = fileType === 'PDF' ? 'Öppna PDF' : 'Ladda ner';
      actionWrap.appendChild(link);
    } else {
      actionWrap.appendChild(createElement('span', 'vi-product-library__document-unavailable', 'Ej tillgänglig'));
    }

    card.appendChild(body);
    card.appendChild(actionWrap);
    return card;
  }

  function renderProduct(product) {
    var safeProduct = isObject(product) ? product : {};
    var title = text(safeProduct.title, 'Produkt utan namn');
    var producer = text(safeProduct.producer, 'Producent ej angiven');
    var documents = Array.isArray(safeProduct.documents) ? safeProduct.documents : [];
    var card = createElement('article', 'vi-product-library__product-card');
    var body = createElement('div', 'vi-product-library__product-body');
    var heading = createElement('h2', 'vi-product-library__product-title', title);
    var producerElement = createElement('p', 'vi-product-library__producer', producer);
    var metaList = createElement('dl', 'vi-product-library__meta-list');
    var documentsWrap = createElement('div', 'vi-product-library__documents');
    var documentsTitle = createElement('h3', 'vi-product-library__documents-title', 'Dokument');

    card.appendChild(renderImage(safeProduct, title));

    body.appendChild(producerElement);
    body.appendChild(heading);
    metaList.appendChild(renderMeta('Senast köpt', formatDate(safeProduct.lastPurchasedAt)));
    metaList.appendChild(renderMeta('Dokument', formatDocumentCount(documents.length)));
    body.appendChild(metaList);

    documentsWrap.appendChild(documentsTitle);

    if (documents.length > 0) {
      documents.forEach(function (documentData) {
        documentsWrap.appendChild(renderDocument(isObject(documentData) ? documentData : {}));
      });
    } else {
      documentsWrap.appendChild(createElement('p', 'vi-product-library__no-documents', 'Inga dokument tillgängliga ännu.'));
    }

    body.appendChild(documentsWrap);
    card.appendChild(body);
    return {
      element: card,
      documentCount: documents.length
    };
  }

  function renderProducts(root, products) {
    var grid = root.querySelector('[data-vipl-grid]');
    var totalDocuments = 0;

    if (!grid) return;
    clear(grid);

    if (!products.length) {
      showState(root, 'empty');
      return;
    }

    products.forEach(function (product) {
      var rendered = renderProduct(product);
      totalDocuments += rendered.documentCount;
      grid.appendChild(rendered.element);
    });

    showProducts(root, products.length, totalDocuments);
  }

  function handleError(root, error) {
    if (isAccessError(error)) {
      showState(root, 'access');
      return;
    }
    showState(root, 'error');
  }

  function init(root) {
    var endpoint = root.getAttribute('data-endpoint') || DEFAULT_ENDPOINT;
    if (root.getAttribute('data-vipl-ready') === 'true') return;
    root.setAttribute('data-vipl-ready', 'true');
    showState(root, 'loading');

    fetchJson(endpoint)
      .then(function (payload) {
        if (!isValidResponse(payload)) {
          throw createApiError('invalid_shape', 0, payload);
        }
        renderProducts(root, payload.data.products);
      })
      .catch(function (error) {
        handleError(root, error);
      });
  }

  function boot() {
    var roots = document.querySelectorAll(ROOT_SELECTOR);
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
